import { slugify } from "./string";

/**
 * Builds a URLSearchParams from a record, omitting undefined values. Array
 * values are appended as repeated keys; scalars are stringified.
 */
export function buildUrlSearchParams(
  values: Record<string, string | number | boolean | string[] | undefined>,
): URLSearchParams {
  const urlSearchParams = new URLSearchParams();
  for (const [key, value] of Object.entries(values)) {
    if (Array.isArray(value)) {
      for (const item of value) {
        urlSearchParams.append(key, item);
      }
    } else if (value !== undefined) {
      urlSearchParams.set(key, String(value));
    }
  }
  return urlSearchParams;
}

export function isValidHttpUrl(
  url: string,
  options: {
    allowBlockedHostnames?: boolean;
    allowedHostnames?: string[];
  } = {},
) {
  const validUrl = normalizeUrlIncludingProtocolRelative(url);
  if (!validUrl) {
    return false;
  }
  if (validUrl.protocol !== "http:" && validUrl.protocol !== "https:") {
    return false;
  }
  if (
    options.allowBlockedHostnames === false &&
    isBlockedHostname(validUrl.hostname)
  ) {
    return false;
  }
  if (
    options.allowedHostnames &&
    !matchesAllowedHostname({
      hostname: validUrl.hostname,
      allowedHostnames: options.allowedHostnames,
    })
  ) {
    return false;
  }
  return true;
}

function matchesAllowedHostname({
  hostname,
  allowedHostnames,
}: {
  hostname: string;
  allowedHostnames: string[];
}) {
  const normalized = hostname.toLowerCase();
  return allowedHostnames.some((allowedHostname) => {
    const normalizedAllowed = allowedHostname.toLowerCase();
    return (
      normalized === normalizedAllowed ||
      normalized.endsWith(`.${normalizedAllowed}`)
    );
  });
}

function isBlockedHostname(hostname: string): boolean {
  const normalized = hostname.toLowerCase();
  if (normalized === "localhost" || normalized.endsWith(".localhost")) {
    return true;
  }
  if (
    /^(::1|fc[\da-f]{0,2}:|fd[\da-f]{0,2}:|fe80:)/.test(normalized) ||
    normalized.startsWith("::ffff:")
  ) {
    return true;
  }
  const ipv4Match = /^(\d{1,3})\.(\d{1,3})/.exec(normalized);
  if (ipv4Match) {
    const first = Number(ipv4Match[1]);
    const second = Number(ipv4Match[2]);
    if (Number.isNaN(first) || Number.isNaN(second)) {
      return true;
    }
    return (
      first === 0 ||
      first === 127 ||
      first === 10 ||
      (first === 172 && second >= 16 && second <= 31) ||
      (first === 192 && second === 168) ||
      (first === 169 && second === 254)
    );
  }
  return false;
}

/**
 * Tries to parse and validate the given string as a URL, including protocol-relative
 * URLs. Returns a URL if parsing is possible, otherwise null.
 *
 * Note: Javascript URLs don't recognize protocol-relative URLs (e.g. //google.com)
 * as valid, so we need special casing here. If protocol-relative, the returned
 * url will have an https protocol. This default is arbitrary but backed up by best
 * practice: https://webhint.io/docs/user-guide/hints/hint-no-protocol-relative-urls/
 */
export function normalizeUrlIncludingProtocolRelative(url: string): URL | null {
  let validUrl;
  try {
    validUrl = new URL(url);
  } catch {
    if (url.startsWith("//")) {
      try {
        validUrl = new URL(`https:${url}`);
      } catch {
        return null;
      }
    } else {
      return null;
    }
  }
  return validUrl;
}

export function normalizeCustomDomain(input: string): string {
  return input
    .trim()
    .replace(/^https?:\/\//i, "")
    .replace(/\/.*$/, "")
    .replace(/\.$/, "")
    .toLowerCase();
}

// NOTE (Noah, 2026-06-12, REPL-27928): Centralized validation for
// *.preview.replointernal.com origins (Vercel preview deployments)
const PREVIEW_INTERNAL_ORIGIN_RE =
  /^https:\/\/[\da-z-]+\.preview\.replointernal\.com$/;
const PREVIEW_INTERNAL_HOSTNAME_RE = /^[\da-z-]+\.preview\.replointernal\.com$/;

/**
 * Returns true if `origin` is a valid Replo Vercel preview deployment origin.
 * Matches `https://<slug>.preview.replointernal.com`, e.g.
 *
 * Use this when you have a full origin string (e.g. from `request.headers.origin`).
 */
export function isPreviewOrigin(origin: string): boolean {
  return PREVIEW_INTERNAL_ORIGIN_RE.test(origin);
}

/**
 * Returns true if `hostname` belongs to a Replo Vercel preview deployment.
 * Matches `<slug>.preview.replointernal.com`, e.g.
 *
 * Use this when you've already parsed the URL and have a bare hostname.
 */
export function isPreviewInternalHostname(hostname: string): boolean {
  return PREVIEW_INTERNAL_HOSTNAME_RE.test(hostname);
}

export function normalizeUrlScheme(url: string) {
  return url.startsWith("//") ? `https:${url}` : url;
}

/**
 * Truncates a domain name by shortening the subdomain portion while preserving
 * the main domain suffix. Useful for displaying long domain names in UI.
 *
 * @param domain - The domain to truncate (e.g., "very-long-subdomain.example.com")
 * @param maxLength - Maximum length of the truncated domain (default: 40)
 * @returns Truncated domain with ellipsis (e.g., "very-long-sub...example.com")
 */
export function truncateDomain(domain: string, maxLength = 60): string {
  // If domain is short enough, return as-is
  if (domain.length <= maxLength) {
    return domain;
  }

  // Split the domain into parts
  const parts = domain.split(".");

  // Need at least 2 parts (subdomain.domain)
  if (parts.length < 2) {
    return domain.length <= maxLength
      ? domain
      : `${domain.slice(0, maxLength - 3)}...`;
  }

  // For common patterns like *.reploshops.com, preserve the last 2 parts
  // For regular domains, preserve the last 2 parts (domain.tld)
  const mainDomainParts = parts.slice(-2);
  const subdomainParts = parts.slice(0, -2);

  const mainDomain = mainDomainParts.join(".");

  // If even just the main domain is too long, truncate it
  if (mainDomain.length >= maxLength - 3) {
    return `${mainDomain.slice(0, maxLength - 3)}...`;
  }

  // Calculate available space for subdomain
  const availableSpace = maxLength - mainDomain.length - 4; // 4 for "..." and "."

  if (subdomainParts.length === 0) {
    return domain;
  }

  const subdomainString = subdomainParts.join(".");

  if (subdomainString.length <= availableSpace) {
    return domain;
  }

  // Truncate subdomain portion
  const truncatedSubdomain = subdomainString.slice(
    0,
    Math.max(1, availableSpace),
  );

  return `${truncatedSubdomain}...${mainDomain}`;
}

/**
 * Validates that a route path is properly formatted and URL-safe.
 * Routes must start with / and contain only valid slug segments.
 *
 * @param path - The route path to validate
 * @returns true if the path is a valid route, false otherwise
 */
export const isValidRoutePath = (path: string): boolean => {
  // Must start with a slash
  if (!path.startsWith("/")) {
    return false;
  }

  // Remove leading slash and split by remaining slashes
  const segments = path.slice(1).split("/");

  // Empty path (just "/") is valid
  if (segments.length === 1 && segments[0] === "") {
    return true;
  }

  // Each segment must be a valid slug
  return segments.every((segment) => {
    if (segment === "") {
      return false;
    }
    return segment === slugify(segment) && segment.length > 0;
  });
};

/**
 * Parses a URL, returning null instead of throwing. Pass `base` to resolve a
 * relative value such as a site path.
 */
export function parseUrl(value: string, base?: string): URL | null {
  try {
    return new URL(value, base);
  } catch {
    return null;
  }
}

/**
 * File extension from a URL's path, lowercased, defaulting to "jpg" for
 * extensionless paths (product imagery is the primary consumer).
 */
export function fileExtensionFromUrl(url: string): string {
  const pathname = URL.canParse(url) ? new URL(url).pathname : url;
  const lastSegment = pathname.split("/").pop() ?? "";
  const extension = lastSegment.includes(".")
    ? lastSegment.split(".").pop() ?? ""
    : "";
  return extension.toLowerCase() || "jpg";
}

/**
 * Registration inputs are attacker-typeable, and the pipeline fetches them
 * server-side — so only plain public https(+http) origins pass: no
 * credentials, no ports, no IP literals, no localhost/RFC-6762 style names.
 * Upgrades http to https and strips the fragment by default; `preserveProtocol`
 * keeps the requested scheme.
 */
export function sanitizePublicHttpUrl(
  value: string | null,
  { preserveProtocol = false }: { preserveProtocol?: boolean } = {},
): string | null {
  const trimmed = value?.trim();
  if (!trimmed) {
    return null;
  }
  const withScheme = /^[a-z][a-z0-9+.-]*:/iu.test(trimmed)
    ? trimmed
    : `https://${trimmed}`;
  let url: URL;
  try {
    url = new URL(withScheme);
  } catch {
    return null;
  }
  if (url.protocol !== "https:" && url.protocol !== "http:") {
    return null;
  }
  if (url.username || url.password || url.port) {
    return null;
  }
  const host = url.hostname.toLowerCase();
  if (
    !host.includes(".") ||
    host === "localhost" ||
    host.endsWith(".") ||
    host.endsWith(".localhost") ||
    host.endsWith(".local") ||
    host.endsWith(".internal") ||
    host.endsWith(".lan") ||
    /^\d{1,3}(\.\d{1,3}){3}$/u.test(host) ||
    host.startsWith("[")
  ) {
    return null;
  }
  if (!preserveProtocol) {
    url.protocol = "https:";
  }
  url.hash = "";
  return url.toString();
}

// A URL a third-party fetcher (a messaging provider, an unfurler) can reach:
// public https with none of the private/loopback shapes `sanitizePublicHttpUrl`
// rejects.
export function isPublicHttpsUrl(value: string): boolean {
  return (
    sanitizePublicHttpUrl(value, { preserveProtocol: true })?.startsWith(
      "https://",
    ) ?? false
  );
}

export function safeHost(url: string): string | null {
  try {
    return new URL(url).hostname.toLowerCase();
  } catch {
    return null;
  }
}
